/**
 * A per-facility accent colour, so two windows open on two facilities are
 * distinguishable at a glance rather than by reading the name.
 *
 * This exists because of a specific failure mode: a support or corporate user
 * with two near-identical windows, differing only by a facility name in the
 * toolbar, charting into the wrong resident. The colour is a fast pre-attentive
 * tell — the facility NAME remains the actual answer, and everything drawn
 * with this colour is decorative and `aria-hidden`, so nothing is conveyed by
 * colour alone (WCAG 1.4.1).
 *
 * Hues are spaced by the golden angle so consecutive facility IDs — which is
 * exactly what a corporate group has — land far apart on the wheel instead of
 * as neighbouring shades of the same colour. Saturation and lightness are
 * fixed at values that hold up against both the light and the dark toolbar,
 * so no theme-specific variant is needed.
 */

const GOLDEN_ANGLE = 137.508;

/** Deterministic accent for a facility. Same ID always yields the same colour. */
export function facilityAccentColor(facilityId: number | null | undefined): string {
  if (typeof facilityId !== 'number' || !Number.isFinite(facilityId) || facilityId <= 0) {
    return 'var(--brand-accent)';
  }
  const hue = Math.round((facilityId * GOLDEN_ANGLE) % 360);
  return `hsl(${hue}, 62%, 45%)`;
}
